import React, { useState, useMemo } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import { useBankLens } from './BankLensContext';
import {
    BENCHMARKS, METRIC_INFO,
} from './banksData';
import styles from './BankLensPage.module.css';

/* ── Available comparison metrics ─────────────────────────── */
const COMPARE_METRICS = [
    { key: 'nim', group: 'Profitability' },
    { key: 'roa', group: 'Profitability' },
    { key: 'roe', group: 'Profitability' },
    { key: 'cti', group: 'Profitability', label: 'Cost/Income' },
    { key: 'gnpa', group: 'Asset Quality' },
    { key: 'nnpa', group: 'Asset Quality' },
    { key: 'pcr', group: 'Asset Quality' },
    { key: 'car', group: 'Capital' },
    { key: 'cet1', group: 'Capital' },
    { key: 'ldr', group: 'Liquidity' },
];

const GROUPS = ['Profitability', 'Asset Quality', 'Capital', 'Liquidity'];

/* ── Tooltip renderer ─────────────────────────────────────── */
function ChartTip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className={styles.rechartsTip}>
            <div className={styles.rechartsTipLabel}>{label}</div>
            {payload.map((p, i) => (
                <div key={i} className={styles.rechartsTipRow}>
                    <span className={styles.rechartsTipDot} style={{ background: p.color }} />
                    <span className={styles.rechartsTipName}>{p.name}</span>
                    <span className={styles.rechartsTipVal}>
                        {p.value != null ? `${p.value.toFixed(2)}%` : '—'}
                    </span>
                </div>
            ))}
        </div>
    );
}

export default function BankLensCompare({ onSelectBank }) {
    const { banks: BANKS, getMetrics, getLatestMetrics, getStatus } = useBankLens();
    const [selectedBanks, setSelectedBanks] = useState(() =>
        BANKS.slice(0, 4).map(b => b.ticker)
    );
    const [metric, setMetric] = useState('roe');
    const [activeGroup, setActiveGroup] = useState('Profitability');

    const toggleBank = (ticker) => {
        setSelectedBanks(prev =>
            prev.includes(ticker)
                ? prev.filter(t => t !== ticker)
                : [...prev, ticker]
        );
    };

    const info = METRIC_INFO[metric] || METRIC_INFO.roe;
    const bm = BENCHMARKS[metric];
    const metricLabel = COMPARE_METRICS.find(m => m.key === metric)?.label || info?.label || metric;

    /* ── Trend chart data (FY2016-FY2026) ──── */
    const trendData = useMemo(() => {
        const years = Array.from({ length: 11 }, (_, i) => 2016 + i);
        return years.map(y => {
            const row = { year: `FY${y}` };
            selectedBanks.forEach(ticker => {
                const bank = BANKS.find(b => b.ticker === ticker);
                const data = getMetrics(ticker);
                const match = data.find(d => d.y === y);
                if (match && bank) row[bank.shortName] = match[metric];
            });
            return row;
        });
    }, [selectedBanks, metric, BANKS, getMetrics]);

    /* ── Ranked bars (sorted by latest value) ──── */
    const rankedBanks = useMemo(() => {
        const items = selectedBanks.map(ticker => {
            const bank = BANKS.find(b => b.ticker === ticker);
            const latest = getLatestMetrics(ticker);
            return { bank, value: latest[metric], ticker };
        }).filter(d => d.value != null);

        const inverted = bm?.inverted;
        items.sort((a, b) => inverted ? a.value - b.value : b.value - a.value);
        return items;
    }, [selectedBanks, metric, bm, BANKS, getLatestMetrics]);

    const maxBar = useMemo(() => {
        if (!rankedBanks.length) return 1;
        return Math.max(...rankedBanks.map(d => Math.abs(d.value)), 1);
    }, [rankedBanks]);

    return (
        <>
            <div className={styles.mainHeader}>
                <h1 className={styles.mainTitle}>Side-by-Side Comparison</h1>
                <p className={styles.mainSubtitle}>
                    Select banks and metrics to compare · FY2016–Q3 FY26
                </p>
            </div>

            {/* ── Controls ──── */}
            <div className={styles.compareControls}>
                <div className={styles.controlGroup}>
                    <span className={styles.controlLabel}>Banks</span>
                    <div className={styles.bankSelector}>
                        {BANKS.map(b => (
                            <button
                                key={b.ticker}
                                className={selectedBanks.includes(b.ticker) ? styles.bankChipActive : styles.bankChip}
                                onClick={() => toggleBank(b.ticker)}
                            >
                                <span className={styles.chipDot} style={{ background: b.color }} />
                                {b.shortName}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Metric tabs ──── */}
            <div className={styles.tabsBar}>
                {GROUPS.map(g => (
                    <button
                        key={g}
                        className={activeGroup === g ? styles.tabActive : styles.tab}
                        onClick={() => {
                            setActiveGroup(g);
                            const first = COMPARE_METRICS.find(m => m.group === g);
                            if (first) setMetric(first.key);
                        }}
                    >
                        {g}
                    </button>
                ))}
            </div>

            {/* Sub-tabs for metrics in group */}
            <div className={styles.tabsBar} style={{ marginTop: -8, borderBottomColor: '#1e2a42' }}>
                {COMPARE_METRICS.filter(m => m.group === activeGroup).map(m => (
                    <button
                        key={m.key}
                        className={metric === m.key ? styles.tabActive : styles.tab}
                        onClick={() => setMetric(m.key)}
                    >
                        {m.label || METRIC_INFO[m.key]?.label || m.key.toUpperCase()}
                    </button>
                ))}
            </div>

            {/* ── Trend line chart ──── */}
            <div className={styles.compareTrendCard}>
                <div className={styles.cardTitle}>
                    {metricLabel} — 10-Year Trend
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#7a8698' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#7a8698' }} width={45} />
                        <Tooltip content={<ChartTip />} />
                        {bm && (
                            <ReferenceLine
                                y={bm.good}
                                stroke="#7a8698"
                                strokeDasharray="6 4"
                                label={{
                                    value: `${bm.good}% Target`,
                                    fill: '#7a8698',
                                    fontSize: 10,
                                    position: 'right',
                                }}
                            />
                        )}
                        {selectedBanks.map(ticker => {
                            const bank = BANKS.find(b => b.ticker === ticker);
                            if (!bank) return null;
                            return (
                                <Line
                                    key={ticker}
                                    type="monotone"
                                    dataKey={bank.shortName}
                                    stroke={bank.color}
                                    strokeWidth={2}
                                    dot={{ r: 3, strokeWidth: 0 }}
                                    activeDot={{ r: 5 }}
                                    name={bank.shortName}
                                    connectNulls
                                />
                            );
                        })}
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* ── Ranked horizontal bars ──── */}
            <div className={styles.compareBarCard}>
                <div className={styles.cardTitle}>
                    {metricLabel} — Latest Values (Ranked)
                </div>
                {rankedBanks.map((item, idx) => {
                    const barWidth = Math.max((Math.abs(item.value) / maxBar) * 100, 2);
                    const status = getStatus(item.value, metric);
                    const color = status === 'good' ? '#20b072' : status === 'ok' ? '#e5a93c' : status === 'bad' ? '#e04848' : '#7a8698';
                    return (
                        <div
                            key={item.ticker}
                            className={styles.compareBarRow}
                            style={{ cursor: 'pointer' }}
                            onClick={() => onSelectBank(item.ticker)}
                        >
                            <span className={styles.compareBarName}>
                                <span style={{ color: item.bank.color, marginRight: 6 }}>●</span>
                                {item.bank.shortName}
                            </span>
                            <div className={styles.compareBarTrack}>
                                <div
                                    className={styles.compareBarFill}
                                    style={{ width: `${barWidth}%`, background: color }}
                                />
                            </div>
                            <span className={styles.compareBarValue} style={{ color }}>
                                {item.value.toFixed(2)}%
                            </span>
                        </div>
                    );
                })}
                {rankedBanks.length === 0 && (
                    <div style={{ padding: 24, textAlign: 'center', color: '#7a8698' }}>
                        Select at least one bank to compare
                    </div>
                )}
            </div>
        </>
    );
}
